import { Localized } from "@fluent/react"
import {
    Box,
    ButtonGroup,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControl,
    FormHelperText,
    Grid,
    TextField,
} from "@mui/material"
import { useCallback, useEffect, useState } from "react"
import { useAppDispatch, useAppSelector } from "../../../app/hooks"
import { CancelButton } from "../../../app/components/CancelButton"
import { SaveButton } from "../../../app/components/SaveButton"
import { selectUserCompanyId } from "../../user/userSlice"
import {
    createStructureDefinition,
    StructureDefinitionDto,
    StructureDefinitionTypeEnum,
    updateStructureDefinition,
} from "../structureSlice"

const RequiredError = () => (
    <Localized id="field-is-required">
        <span>_Pole jest wymagane</span>
    </Localized>
)

type CreateStructureDialogProps = {
    open?: boolean
    onClose?: () => void
    onSaved?: (definition: StructureDefinitionDto) => void
    mode?: "create" | "edit"
    structure?: StructureDefinitionDto | null
    defaultCode?: string
}

function formatDateForInput(date: Date | string | null | undefined): string {
    if (!date) {
        return ""
    }

    const parsed = typeof date === "string" ? new Date(date) : date
    if (Number.isNaN(parsed.getTime())) {
        return ""
    }

    return parsed.toISOString().split("T")[0]
}

export const CreateStructureDialog = ({
    open = true,
    onClose,
    onSaved,
    mode = "create",
    structure,
    defaultCode,
}: CreateStructureDialogProps) => {
    const dispatch = useAppDispatch()
    const userCompanyId = useAppSelector(selectUserCompanyId)

    const [name, setName] = useState("")
    const [code, setCode] = useState("")
    const [description, setDescription] = useState("")
    const [validFrom, setValidFrom] = useState(formatDateForInput(new Date()))
    const [saving, setSaving] = useState(false)
    const [validationErrors, setValidationErrors] = useState(false)

    useEffect(() => {
        if (!open) {
            return
        }

        if (mode === "edit" && structure) {
            setName(structure.name ?? "")
            setCode(structure.code ?? "")
            setDescription(structure.description ?? "")
            setValidFrom(formatDateForInput(structure.validFrom))
            setValidationErrors(false)
            return
        }
        setName("")
        setCode(defaultCode ?? "")
        setDescription("")
        setValidFrom(formatDateForInput(new Date()))
        setValidationErrors(false)
    }, [open, mode, structure, defaultCode])

    const nameError = validationErrors && name.trim() === ""
    const validFromError = validationErrors && validFrom.trim() === ""

    const handleCloseButtonClick = useCallback(() => {
        if (onClose) {
            onClose()
        }
    }, [onClose])

    const handleSaveButtonClick = async () => {
        setValidationErrors(true)

        if (name.trim() === "" || validFrom.trim() === "") {
            return
        }

        if (!userCompanyId) {
            return
        }

        setSaving(true)
        try {
            const createDto = {
                name: name.trim(),
                code: code.trim(),
                description: description.trim(),
                type: defaultCode === "ORGANIZATIONAL_STRUCTURE" || defaultCode === "CUSTOM_DEVICE_STRUCTURE"
                    ? defaultCode
                    : "GENERAL" as StructureDefinitionTypeEnum,
                companyId: Number(userCompanyId),
                validFrom: new Date(validFrom),
            }

            if (mode === "edit" && structure) {
                const updateResult = await dispatch(updateStructureDefinition({
                    id: structure.id,
                    name: name.trim(),
                    code: code.trim(),
                    description: description.trim(),
                    type: structure.type,
                    validFrom: new Date(validFrom),
                    validTo: structure.validTo ? new Date(structure.validTo) : null,
                }))

                if (updateStructureDefinition.fulfilled.match(updateResult)) {
                    if (onSaved) {
                        onSaved(updateResult.payload)
                    }
                    handleCloseButtonClick()
                } else {
                    console.error(updateResult.payload)
                }
                return
            }

            const createResult = await dispatch(createStructureDefinition(createDto))

            if (createStructureDefinition.fulfilled.match(createResult)) {
                if (onSaved) {
                    onSaved(createResult.payload)
                }
                handleCloseButtonClick()
            } else {
                console.error(createResult.payload)
            }
        } finally {
            setSaving(false)
        }
    }

    return (
        <Dialog open={open} fullWidth maxWidth="sm" onClose={handleCloseButtonClick}>
            <DialogTitle>
                {mode === "edit" ? (
                    <Localized id="structures-edit-title">
                        <span>_Edycja struktury</span>
                    </Localized>
                ) : (
                    <Localized id="structures-create-title">
                        <span>_Tworzenie struktury</span>
                    </Localized>
                )}
            </DialogTitle>

            <DialogContent dividers>
                <Box sx={{ pt: 1 }}>
                    <Grid container spacing={2}>
                        {defaultCode === undefined && (
                            <Grid item xs={12}>
                                <TextField
                                    fullWidth
                                    value={code}
                                    onChange={(e) => setCode(e.target.value.toUpperCase().slice(0, 50))}
                                    inputProps={{ maxLength: 50 }}
                                    label={<Localized id="structures-list-code"><span>_Kod</span></Localized>}
                                />
                            </Grid>
                        )}

                        <Grid item xs={12}>
                            <FormControl fullWidth error={nameError}>
                                <TextField
                                    required
                                    fullWidth
                                    value={name}
                                    error={nameError}
                                    onChange={(e) => setName(e.target.value)}
                                    label={<Localized id="structures-list-name"><span>_Nazwa</span></Localized>}
                                />
                                {nameError && <FormHelperText><RequiredError /></FormHelperText>}
                            </FormControl>
                        </Grid>


                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                value={description}
                                onChange={(e) => setDescription(e.target.value)}
                                label={<Localized id="structures-list-description"><span>_Opis</span></Localized>}
                            />
                        </Grid>

                        <Grid item xs={12}>
                            <FormControl fullWidth error={validFromError}>
                                <TextField
                                    required
                                    fullWidth
                                    type="date"
                                    InputLabelProps={{ shrink: true }}
                                    value={validFrom}
                                    error={validFromError}
                                    onChange={(e) => setValidFrom(e.target.value)}
                                    label={<Localized id="structures-list-valid-from"><span>_Obowiązuje od</span></Localized>}
                                />
                                {validFromError && <FormHelperText><RequiredError /></FormHelperText>}
                            </FormControl>
                        </Grid>
                    </Grid>
                </Box>
            </DialogContent>

            <DialogActions>
                <ButtonGroup variant="contained" color="secondary">
                    <SaveButton loading={saving} onClick={handleSaveButtonClick} />
                    <CancelButton loading={saving} onClick={handleCloseButtonClick} />
                </ButtonGroup>
            </DialogActions>
        </Dialog>
    )
}
